import { useQuery } from "@tanstack/react-query";
import { api } from "@/api/client";
import { opcoesPainel, type OpcoesPainel } from "@/api/opcoesPainel";
import { fetchPaged } from "@/api/paged";
import type { TipoInteracao } from "@/features/instituicao-interacao/api";

// Home (e modo TV): mesmos endpoints do overview.controller.js legado, mais os da
// remodelagem em telas (interações por mês e mapa de clientes).

/** instituicao-microservice, POST Instituicao/Overview. Só os campos que a tela usa. */
export interface OverviewGeral {
  ativos?: number;
  trials?: number;
  cancelamentosAnoAtual?: number;
  cancelamentosMesAtual?: number;
  /** Trials iniciados no mês atual, qualquer que seja a situação hoje. */
  trialsMesAtual?: number;
  quantidadeIdosos?: number;
  quantidadeUsuarios?: number;
}

/** Séries mensais; `label` vem como "yyyy-MM". */
export interface GraficoAbsoluto {
  label: string[];
  serieTotalAtivos: number[];
  serieTrialsMes: number[];
  serieCanceladosMes: number[];
}

export type MetricaSistema =
  | "idosos"
  | "evolucoes"
  | "usuarios"
  | "administracoes"
  | "checagens"
  | "movimentacoesEstoque"
  | "sinaisVitais"
  | "receitasMedicas"
  | "emails"
  | "notificacoesPortal";

/** Total acumulado e o que entrou no mês atual, ambos até ontem (coleta diária). */
export interface TotalMes {
  total: number;
  mes: number;
}

/** relatorio-microservice, POST Graficos/Overview. Campos nulos não vêm. */
export interface OverviewGraficos {
  /** Soma da coleta mais recente; ausente onde ainda não há coleta. */
  evolucaoTotal?: number;
  /** Ativos na última coleta do mês anterior: base da variação de ativos no mês. */
  ativosFimMesAnterior?: number;
  /** Vazio quando ainda não há coleta. `usuarios` são profissionais (usuário nasce de um). */
  sistema?: Partial<Record<MetricaSistema, TotalMes>>;
  graficoAbsoluto: GraficoAbsoluto;
}

export type TipoInteracaoCs = Extract<TipoInteracao, "SU" | "CB" | "CN" | "TR">;

/** instituicao-microservice, POST InstituicaoInteracao/Overview: 12 meses até o atual. */
export interface InteracoesPorMes {
  meses: string[];
  series: Record<TipoInteracaoCs, number[]>;
}

/** instituicao-microservice, POST Instituicao/MapaClientes: só clientes ativos. */
export interface MapaClientes {
  cidades: { codigoIbge: string; clientes: number }[];
  /** Ativos sem código IBGE (sem CEP ou CEP que o ViaCEP não resolveu). */
  clientesSemLocalizacao: number;
  /** Os mesmos sem código, pelo município/UF do cadastro (texto livre): casados por nome no front. */
  semCodigo?: { municipio: string; estado?: string; clientes: number }[];
}

export interface InteracaoRecente {
  id: number;
  dataInteracao?: string;
  descricao?: string;
  tipoInteracao?: TipoInteracao;
  usuarioResponsavel?: { nome?: string };
  instituicao?: { nomeFantasia?: string };
}

export async function getOverviewGeral(): Promise<OverviewGeral> {
  const { data } = await api.post<OverviewGeral>("instituicao/Instituicao/Overview", {});
  return data;
}

export async function getOverviewGraficos(): Promise<OverviewGraficos> {
  const { data } = await api.post<OverviewGraficos>("relatorio/Graficos/Overview", {});
  return data;
}

export async function getInteracoesPorMes(): Promise<InteracoesPorMes> {
  const { data } = await api.post<InteracoesPorMes>("instituicao/InstituicaoInteracao/Overview", {});
  return data;
}

export async function getMapaClientes(): Promise<MapaClientes> {
  const { data } = await api.post<MapaClientes>("instituicao/Instituicao/MapaClientes", {});
  return data;
}

export async function getInteracoesRecentes(): Promise<InteracaoRecente[]> {
  const page = await fetchPaged<InteracaoRecente, object>("instituicao/InstituicaoInteracao", {
    page: 0,
    size: 50,
    sort: { field: "dataInteracao", direction: "DESC" },
    filtro: {},
  });
  return page.content;
}

const KEY = ["overview"] as const;

export function useOverviewGeral(opcoes: OpcoesPainel = {}) {
  return useQuery({ queryKey: [...KEY, "geral"], queryFn: getOverviewGeral, ...opcoesPainel(opcoes) });
}

export function useOverviewGraficos(opcoes: OpcoesPainel = {}) {
  return useQuery({ queryKey: [...KEY, "graficos"], queryFn: getOverviewGraficos, ...opcoesPainel(opcoes) });
}

export function useInteracoesPorMes(opcoes: OpcoesPainel = {}) {
  return useQuery({ queryKey: [...KEY, "interacoes-mes"], queryFn: getInteracoesPorMes, ...opcoesPainel(opcoes) });
}

export function useMapaClientes(opcoes: OpcoesPainel = {}) {
  return useQuery({ queryKey: [...KEY, "mapa-clientes"], queryFn: getMapaClientes, ...opcoesPainel(opcoes) });
}

/** O legado rebuscava a cada 60s com um setInterval que nunca era limpo; aqui o polling morre com a tela.
 * No modo TV segue o intervalo dos demais painéis. */
export function useInteracoesRecentes(opcoes: OpcoesPainel = {}) {
  return useQuery({
    queryKey: [...KEY, "interacoes"],
    queryFn: getInteracoesRecentes,
    refetchInterval: 60_000,
    meta: { silent: true },
    ...opcoesPainel(opcoes),
  });
}
